import { evaluateRelation } from "@editor/items/relation/Relation";
import type { ImpulseItem } from "./ImpulseItem";
import type { RelationItem } from "@editor/items/relation/RelationItem";
import type { FlowBranchItem } from "@editor/items/flow/FlowBranchItem";
import type { ExpressionValue } from "@editor/items/expression/expressionFunctions";

class Impulse implements ImpulseItem {
  type: string;

  pointsToAdd: number | null;
  valueToSet?: number | null;
  valueBefore?: number;
  valueAfter?: number;

  sourceValues: {
    id: number | string;
    type: string;
    value: number | null;
    valueToSet?: number | null;
    effectType: "effect" | "cost" | "net";
  }[];

  constructor(impulseData: ImpulseItem) {
    if (impulseData.pointsToAdd == null) {
      this.pointsToAdd = null;
    } else {
      this.pointsToAdd = roundPoints(impulseData.pointsToAdd);
    }

    this.type = "impulse";
    this.valueToSet = impulseData.valueToSet;
    this.valueBefore = impulseData.valueBefore; // save the value just before the impulse was applied
    this.valueAfter = impulseData.valueAfter; // save the value just after the impulse was applied
    this.sourceValues = impulseData.sourceValues; // where does the impulse come from { roundIndex, id, type, value }
  }

  static add(a?: ImpulseItem, b?: ImpulseItem) {
    // return the valid impulse if the other is not valid
    if (a == null && b != null) return new Impulse(b);
    if (b == null && a != null) return new Impulse(a);
    if (a == null || b == null) {
      throw new Error("Impulse A and B are null");
    }

    return new Impulse({
      type: "impulse",
      pointsToAdd: addPoints(a.pointsToAdd, b.pointsToAdd),
      valueToSet: b.valueToSet ?? a.valueToSet ?? null,
      sourceValues: a.sourceValues.concat(b.sourceValues)
    });
  }

  /**
   * Return the impulse a relation would pass through in a certain round
   * @param roundStartValues The starting values of all rounds. Key: roundIndex
   * @returns Return Impulse
   */
  static createFromRelation(
    relation: RelationItem,
    rounds: {
      startElementValues: { [elementUid: string]: ExpressionValue };
    }[],
    roundIndex: number
  ) {
    if (relation.updateType != "change") return undefined;

    const delay = relation.delay || 0;
    const delayedRoundIndex = roundIndex - delay;
    const srcImpulseRoundIndex = delayedRoundIndex - 1;

    // don't try to pass on impulses if the delay reaches back to before creation:
    if (srcImpulseRoundIndex < 0) return undefined;

    // get the item to watch for changes
    const srcId = relation.startItemId;
    const srcType = relation.startItemType;

    let previousValue = 0;
    let currentValue = 0;

    // get the impulse of the src item of the relation
    const srcRound = rounds[srcImpulseRoundIndex];
    previousValue = Number(srcRound?.startElementValues[`${srcType}_${srcId}`]?.value || 0);

    const delayedRound = rounds[delayedRoundIndex];
    currentValue = Number(delayedRound?.startElementValues[`${srcType}_${srcId}`]?.value || 0);

    if (previousValue == null || currentValue == null) return undefined;

    if (previousValue === currentValue) return undefined;

    const valA = evaluateRelation(relation, previousValue);
    const valB = evaluateRelation(relation, currentValue);
    const transformedValue = valB - valA;

    const pointsToAdd = roundPoints(transformedValue);

    // don't add sources unter that threshold:
    if (Math.abs(pointsToAdd) < 0.01) return undefined;

    return new Impulse({
      pointsToAdd: transformedValue,
      sourceValues: [
        {
          type: relation.type,
          id: relation.id,
          value: transformedValue,
          effectType: "net"
        }
      ]
    });
  }

  static createFromFlow(flowBranch: FlowBranchItem, controlElementValue: number) {
    const pointsToAdd = evaluateRelation(flowBranch, controlElementValue);

    // skip if the connection does not change any points.
    if (pointsToAdd === 0) return undefined;

    // create a net impulse and return it
    return new Impulse({
      pointsToAdd: pointsToAdd,
      sourceValues: [
        {
          type: "flowBranch",
          id: flowBranch.id,
          value: pointsToAdd,
          effectType: "net"
        }
      ]
    });
  }
}

/**
 * Round the points of an impulse
 */
function roundPoints(points: number = 0) {
  return Math.round(points * 100) / 100;
}

function addPoints(a: number | null, b: number | null) {
  if (a == null) return b;
  if (b == null) return a;
  return a + b;
}

export default Impulse;
